// src/encounter/lib/video-join-window.ts
//
// When a Virtual visit's Connect may start the call. Flutter `encounter_screen.dart:1208` `canStartCall`:
//
//     final isBeforeStart = (start?.difference(now).inSeconds ?? 1000) <= 600;
//     final isAfterEnd = ((end?.add(Duration(hours: 2)))?.difference(now).inSeconds ?? 1000) > 0;
//     return (isBeforeStart && isAfterEnd);
//
// and its Connect tap (:1320) also refuses a Fulfilled appointment. So: from 10 minutes before the start until 2 hours
// after the end, and never once the appointment is Fulfilled. Leave is never gated (the host keeps it while in a call).
//
// Why the PA needs it: the video service refuses an early join (dev /api/VideoApi/join: 400 "Join 10 minutes before
// the meeting time"), and the clinician saw only a spinner. Times are read like every other appointment time in the
// app (`new Date(iso)`, the browser zone — see shared/lib/practice-time.ts).

import { practiceTimeZone } from '@/shared/lib/practice-time';

/** Flutter: `difference(now).inSeconds <= 600`. */
export const VIDEO_OPENS_BEFORE_START_MS = 10 * 60 * 1000;
/** Flutter: `end.add(Duration(hours: 2))`. */
export const VIDEO_CLOSES_AFTER_END_MS = 2 * 60 * 60 * 1000;

export type VideoJoinWindow =
  /** Connect starts the call. */
  | { state: 'open'; closesAt: Date | null }
  /** Too early: Connect opens at `opensAt`. */
  | { state: 'early'; opensAt: Date }
  /** More than 2 hours after the end. */
  | { state: 'ended' }
  /** The appointment is Fulfilled (Flutter: "appointment is completed"). */
  | { state: 'completed' }
  /** The start is not known yet (still loading): no Connect, as in Flutter, and no reason to show. */
  | { state: 'unknown' };

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function videoJoinWindow(opts: {
  start: string | null | undefined;
  end: string | null | undefined;
  statusCode: string | null | undefined;
  now: Date;
}): VideoJoinWindow {
  if (opts.statusCode === 'Fulfilled') return { state: 'completed' };
  const start = parse(opts.start);
  // Flutter: a null start reads as 1000 s away, which is outside the 600 s window, so Connect stays off.
  if (!start) return { state: 'unknown' };
  const opensAt = new Date(start.getTime() - VIDEO_OPENS_BEFORE_START_MS);
  if (opts.now.getTime() < opensAt.getTime()) return { state: 'early', opensAt };
  const end = parse(opts.end);
  // Flutter: a null end reads as 1000 s ahead, so the window has no close.
  if (!end) return { state: 'open', closesAt: null };
  const closesAt = new Date(end.getTime() + VIDEO_CLOSES_AFTER_END_MS);
  if (opts.now.getTime() >= closesAt.getTime()) return { state: 'ended' };
  return { state: 'open', closesAt };
}

/** The next instant the window changes state, or null when it never changes again. */
export function nextVideoJoinChange(w: VideoJoinWindow): Date | null {
  if (w.state === 'early') return w.opensAt;
  if (w.state === 'open') return w.closesAt;
  return null;
}

/** "2:50 PM", the app's time format (encounter/lib/visit-time.ts), in the practice-time zone (shared/lib/practice-time.ts). */
function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: practiceTimeZone() });
}

/** Why Connect is not offered, for the line beside the notice; undefined when there is nothing to say. */
export function videoJoinReason(w: VideoJoinWindow): string | undefined {
  switch (w.state) {
    case 'early':
      return `Video opens at ${formatTime(w.opensAt)}`;
    case 'ended':
      return 'Video closed 2 hours after the appointment end';
    case 'completed':
      return 'Video closed: the appointment is completed';
    default:
      return undefined;
  }
}
